// src/drag.js
//
// "Children follow their parent when it is dragged" is not something any
// of the three engines gives for free once real compound/parent nodes are
// off the table (see cluster.js's own reasoning for why they are) — a
// cluster's children here are just nodes connected by an ordinary edge, so
// dragging the cluster moves only the cluster unless something tells the
// rest to follow. This is that something, and it is driver-agnostic: it
// asks the view for the SAME 'drag' event any driver exposes, and moves
// descendants with the SAME `moveNode()` every driver exposes — nothing
// here is Cytoscape-specific.
//
// Stabilising afterwards needs no code at all, on purpose: a driver's
// layout algorithm runs once, when `setData()` calls it, and never again
// on its own — a node dragged by a person (directly, or carried along as
// a descendant here) simply stays where it was left, because nothing ever
// re-runs the layout underneath it. If a driver ever grows a "continuous"
// physics mode, that driver is the one that would need to suspend it
// during a drag — this file would not change.

/**
 * @param {import('./graph-view.js').GraphView} view
 * @returns {{update: (edges: Array<{source: string, target: string}>) => void}}
 *          call `update()` once after every `setData()` — the edges that
 *          existed when this was constructed are not tracked automatically,
 *          since a view has no "current edges" of its own to read back
 */
export function dragFollowsHierarchy(view) {
    let children_of = new Map();

    function update(edges) {
        children_of = new Map();

        for (const edge of edges) {
            const bucket = children_of.get(edge.target) ?? [];
            bucket.push(edge.source);
            children_of.set(edge.target, bucket);
        }
    }

    function descendants_of(id) {
        const found = [];
        const stack = [...(children_of.get(id) ?? [])];

        while (stack.length > 0) {
            const child_id = stack.pop();
            found.push(child_id);
            stack.push(...(children_of.get(child_id) ?? []));
        }

        return found;
    }

    view.on('drag', ({ id, delta }) => {
        for (const child_id of descendants_of(id)) {
            view.driver.moveNode(child_id, delta.dx, delta.dy);
        }
    });

    return { update };
}
